import type { TaskCaseRow } from '../data/tasks';
import {
  TASK_EMPTY_DISPLAY,
  taskStatusKeyFromApi,
  type TaskListScope,
  type UrgencyLevel,
} from '../data/taskStatus';
import { formatDisplayDateTime } from '../utils/dateFormat';

export type ApiTaskRow = {
  id?: string;
  task_id?: string;
  taskId?: string;
  case_request_id?: string;
  caseRequestId?: string;
  reference_no?: string;
  file_ref_no?: string;
  fileRefNo?: string;
  file_category?: string;
  fileCategory?: string;
  sub_file_category?: string;
  subFileCategory?: string;
  submitted_agency?: string;
  submittedAgency?: string;
  submitted_at?: string;
  submittedAt?: string;
  workflow_stage_name?: string;
  workflowStageName?: string;
  current_workflow_stage?: string;
  currentWorkflowStage?: string;
  status_name?: string;
  statusName?: string;
  task_status?: string;
  taskStatus?: string;
  status_code?: string;
  statusCode?: string;
  time_remaining_label?: string;
  timeRemainingLabel?: string;
  time_remaining_display?: string;
  timeRemainingDisplay?: string;
  is_overdue?: boolean;
  isOverdue?: boolean;
  urgency_level?: string;
  urgencyLevel?: string;
  assigned_user_id?: string;
  assignedUserId?: string;
  assigned_user_name?: string;
  assignedUserName?: string;
  assigned_officer_name?: string;
  assignedOfficerName?: string;
  has_new_notification?: boolean;
  hasNewNotification?: boolean;
  has_new_sub_task?: boolean;
  hasNewSubTask?: boolean;
  created_from_intranet?: boolean;
  createdFromIntranet?: boolean;
  is_received_via_forward?: boolean;
  isReceivedViaForward?: boolean;
};

function strField(row: ApiTaskRow, ...keys: (keyof ApiTaskRow)[]): string {
  for (const key of keys) {
    const v = row[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

export function displayOrDash(value: string): string {
  return value.trim() ? value.trim() : TASK_EMPTY_DISPLAY;
}

export function parseUrgencyLevel(raw: string): UrgencyLevel | typeof TASK_EMPTY_DISPLAY {
  const n = raw.trim();
  if (!n) return TASK_EMPTY_DISPLAY;
  const normalized = `${n.charAt(0).toUpperCase()}${n.slice(1).toLowerCase()}`;
  if (normalized === 'High' || normalized === 'Medium' || normalized === 'Low') return normalized;
  return TASK_EMPTY_DISPLAY;
}

function resolveHighlightVariant(
  row: ApiTaskRow,
  scope: TaskListScope,
  statusKey: ReturnType<typeof taskStatusKeyFromApi>,
): TaskCaseRow['highlightVariant'] {
  if (row.hasNewNotification === true || row.has_new_notification === true) return 'notification';
  if (row.hasNewSubTask === true || row.has_new_sub_task === true) return 'subtask';
  if (scope === 'active-linked' && statusKey === 'forwarded') return 'forwarded';
  return 'none';
}

/** Map CMS-CRC-18/20 list item → table row (data dictionary field rules). */
export function adaptApiTaskRow(row: ApiTaskRow, scope: TaskListScope): TaskCaseRow {
  const caseRequestId = strField(row, 'caseRequestId', 'case_request_id', 'id');
  const userTaskId = strField(row, 'taskId', 'task_id');
  const id = caseRequestId || strField(row, 'reference_no', 'file_ref_no', 'fileRefNo');
  const statusName = strField(row, 'taskStatus', 'task_status', 'statusName', 'status_name');
  const statusKey = statusName
    ? taskStatusKeyFromApi(strField(row, 'statusCode', 'status_code'), statusName)
    : 'pending';
  const timeLabel = displayOrDash(
    strField(row, 'timeRemainingDisplay', 'time_remaining_display', 'timeRemainingLabel', 'time_remaining_label'),
  );
  const isOverdue = row.isOverdue === true || row.is_overdue === true || /overdue/i.test(timeLabel);
  const submittedRaw = strField(row, 'submittedAt', 'submitted_at');

  return {
    id: id || caseRequestId,
    userTaskId: userTaskId || undefined,
    caseRequestId: caseRequestId || id,
    ref: displayOrDash(strField(row, 'file_ref_no', 'fileRefNo', 'reference_no')),
    fileCategory: displayOrDash(strField(row, 'fileCategory', 'file_category')),
    subFileCategory: displayOrDash(strField(row, 'subFileCategory', 'sub_file_category')),
    agency: displayOrDash(strField(row, 'submittedAgency', 'submitted_agency')),
    submittedAt: displayOrDash(formatDisplayDateTime(submittedRaw)),
    submittedAtIso: submittedRaw.length >= 10 ? submittedRaw.slice(0, 10) : '',
    stage: displayOrDash(
      strField(row, 'currentWorkflowStage', 'current_workflow_stage', 'workflowStageName', 'workflow_stage_name'),
    ),
    statusKey,
    status: statusName ? statusName : TASK_EMPTY_DISPLAY,
    timeLabel,
    isOverdue,
    hasTimeValue: timeLabel !== TASK_EMPTY_DISPLAY,
    urgencyLevel: parseUrgencyLevel(strField(row, 'urgencyLevel', 'urgency_level')),
    assignedOfficer: displayOrDash(
      strField(
        row,
        'assignedUserName',
        'assigned_user_name',
        'assignedOfficerName',
        'assigned_officer_name',
      ),
    ),
    hasNewNotification: row.hasNewNotification === true || row.has_new_notification === true,
    isReceivedViaForward: row.isReceivedViaForward === true || row.is_received_via_forward === true,
    highlightVariant: resolveHighlightVariant(row, scope, statusKey),
    listScope: scope,
    createdFromIntranet: row.createdFromIntranet === true || row.created_from_intranet === true,
  };
}
